import { Button, Checkbox, CircularProgress, Dialog, DialogTitle } from '@material-ui/core';
import { useContext, useEffect, useState } from 'react';

import { LANDING_URL, MENTAL_CAPACITY_ANALYSIS_PRICE, PURCHASE_PRICE } from '../../config';
import './YourOrder.scss';

import { useAppSelector } from '../../app/hooks';
import OrderContext from '../../contexts/orderContext';
import { selectAffiliate } from '../../features/affiliate/affiliateSlice';
import { Voucher } from '../../models/voucher';
import { Authorization } from '../../services/authorization';
import {
  checkRecordingToken, updateUser, validateVoucher
} from '../../services/axios/requests';
import { GetRecordingToken } from '../../services/localStorage/localstorage';
import { Utils } from '../../services/utils';

interface YourOrderProps {
  next(freeOfChange?: boolean ): void;
};

const YourOrder = (props: YourOrderProps) => {
  const currentOrder = useContext(OrderContext);

  const [isUserLifetimeSubscriptionActive, setIsUserLifetimeSubscriptionActive] = useState(null as boolean | null);

  const [affiliatePrePaidToken, setAffiliatePrePaidToken] = useState(null as string | null);

  const [accepted, setAccepted] = useState(false);
  const [nextClicked, setNextClicked] = useState(false);
  const [lifeTimeUpdates, setLifeTimeUpdates] = useState(currentOrder?.CurrentOrder?.addLifetimeUpdates ?? false);
  const [analysis, setAnalysis] = useState(currentOrder?.CurrentOrder?.addAnalysis ?? false);

  const [discountApplied, setDiscountApplied] = useState("");
  const [voucherCode, setVoucherCode] = useState(currentOrder?.CurrentOrder?.voucherCode ?? '');
  const [totalPrice, setTotalPrice] = useState(PURCHASE_PRICE);
  const [voucher, setVoucher] = useState<Voucher | null>(currentOrder?.CurrentOrder?.voucher ?? null);

  const [inProgress, setInProgress] = useState(false);

  const [showDialogMessage, setShowDialogMessage] = useState('');
  
  const affiliate = useAppSelector(selectAffiliate);

  const checkSubscription = async () => {
    var haveSubscription = await Authorization.GetUserSubscriptionStatus() === true;
    setIsUserLifetimeSubscriptionActive(haveSubscription);
    if (haveSubscription)
      setTotalPrice('0');
  }

  useEffect(() => {
    checkSubscription();
  }, []);

  const next = async (e: any) => {
    e.preventDefault();
    if (accepted) {
      setInProgress(true);
      var isFreeOfCharge = totalPrice === '0'
      if (isFreeOfCharge)
        await updateUser({ signupComplete: true });
      
      props.next(isFreeOfCharge);
    } else {
      setNextClicked(true);
      await new Promise((resolve) => setTimeout(resolve, 1));
      Utils.scrollToError();
    }
  }

  const renderDialog = () => {
    return <Dialog open={true} onClose={() => setShowDialogMessage('')} className="LogoutPopup update-success-dialog">
        <DialogTitle>
          {showDialogMessage}
        </DialogTitle>
        <div className="btn-row" style={{ display: " flex", alignItems: 'center', justifyContent: 'center' }}>
          <Button
            id='ok-btn'
            className="green-button"
            variant="contained"
            color="primary"
            onClick={() => setShowDialogMessage('')}
          >
            Ok
          </Button>
        </div>
      </Dialog>
    
  }

  const validateRecordingToken = async () => {
    const token = GetRecordingToken();
    if (token) {
      const res = await checkRecordingToken(token);
      if (res && res.status === 200 && res.data) {
        setAffiliatePrePaidToken(token);
        setTotalPrice('0');
      }
    }
  }

  // "Amount due" is the discounted snapshot price plus the analysis when it is selected. Every
  // recalculation goes through here, so applying a voucher after adding the analysis keeps the £75.
  const withAnalysis = (discountedPrice: string) => {
    return analysis
      ? (parseFloat(discountedPrice) + parseFloat(MENTAL_CAPACITY_ANALYSIS_PRICE)).toFixed(2)
      : discountedPrice;
  }

  useEffect(() => {
    validateRecordingToken();
    setTotalPrice(withAnalysis(applyAffiliateDiscount()));

    // eslint-disable-next-line
  }, [])

   const renderNextLabel = (text: string) => {
    return inProgress ? <CircularProgress /> : text;
  }

  const getSnapshotPurchasePrice = () => {
    return isUserLifetimeSubscriptionActive ? 0 : parseFloat(PURCHASE_PRICE);
  }

  const applyVoucher = async () => {
    const voucherRes = await validateVoucher(voucherCode);

    currentOrder.SetCurrentOrder({
      ...currentOrder.CurrentOrder, 
      voucherCode: voucherCode, 
      voucher: voucherRes?.data});

    var discountedValue = getSnapshotPurchasePrice();

    if (voucherRes && voucherRes.status === 200 && voucherRes.data) {
      if (voucherRes.data.active) {
        const basePurchasePrice = getSnapshotPurchasePrice();

        setDiscountApplied(voucherRes.data.isFixedPrice
          ? `(discount £${voucherRes.data.value})`
          : `(discount ${voucherRes.data.value}%)`);

        discountedValue = voucherRes.data.isFixedPrice
          ? (basePurchasePrice - parseInt(voucherRes.data.value))
          : basePurchasePrice - (basePurchasePrice * parseInt(voucherRes.data.value) / 100)

        if (discountedValue < 0)
          discountedValue = 0;

        setVoucher(voucherRes.data);

        return discountedValue.toFixed(2)
      }
    }

    return discountedValue.toFixed(2);
  }

  useEffect(() => {
    const applyAnalysis = async () => {
      var discountedPrice = getSnapshotPurchasePrice().toString();

      if (affiliate)
        discountedPrice = applyAffiliateDiscount();
      else if (voucher)
        discountedPrice = await applyVoucher()

      setTotalPrice(withAnalysis(discountedPrice));

      currentOrder.SetCurrentOrder({...currentOrder.CurrentOrder, addAnalysis: analysis})
    }

    applyAnalysis();

  }, [analysis])

  useEffect(() => {
    currentOrder.SetCurrentOrder({ ...currentOrder.CurrentOrder, addLifetimeUpdates: lifeTimeUpdates })
  }, [lifeTimeUpdates])

  const applyAffiliateDiscount = () => {
    if (affiliate && parseInt(affiliate.discount) > 0) {
      var basePurchasePrice = getSnapshotPurchasePrice();
      setDiscountApplied(`(discount ${affiliate.affiliateName} ${affiliate.discount}%)`);

      var discountedValue = basePurchasePrice - (basePurchasePrice * parseInt(affiliate.discount) / 100)

      if (discountedValue < 0)
        discountedValue = 0;

      return discountedValue.toFixed(2)
    }
    else
      return totalPrice
  }

  const isFreeOfCharge = () => {
    // NO PAYMENT when there is Affiliate token, voucher 100% or affiliate discount 100% without lifetime updates.
    if (!analysis && !lifeTimeUpdates) {
      // NO PAYMENT when affiliate token is active
      if (affiliatePrePaidToken)
        return true;

      // NO PAYMENT when voucher discount 100%
      if (voucher && voucher.isFixedPrice === false && parseInt(voucher.value ?? 0) === 100)
        return true;

      // NO PAYMENT when affiliate discount 100%
      if (affiliate && parseInt(affiliate.discount) === 100)
        return true;

      //TODO: NO payment when there is less then 30 days since initial payment

      //TODO: No payment when LU is active
    }

    return false;
  }

  return (
    <form className="YourOrder" 
      onSubmit={next}>
      <div className="bold ft-25 mt-12 ml-24">Choose Your Capacity Vault Options</div>

      <section className='mt-40'>

        <div className="flex-inline accept-condition-check label-box ft-22">
          <div className='flex-inline-left'>
            <div className="label">Capacity Vault Secure Setup for <b>£{getSnapshotPurchasePrice()}</b></div>
          </div>
          <div className="bold"></div>
        </div>

        {/* Voucher code filed should not be available for affiliates */}
        {!affiliate && !affiliatePrePaidToken && isUserLifetimeSubscriptionActive !== true &&
          <div className="flex-inline voucher-row">
            <div className="label ft-18 lower-label">Do you have a voucher?</div>
            <div className='flex-inline ml-26'>
              <input className="ft-18" onChange={e => setVoucherCode(e.target.value)} placeholder="voucher code?" />
              <button
                id='apply-voucher-btn'
                disabled={inProgress}
                className="ft-18 apply-btn" 
                onClick={async e => {
                  e.preventDefault();
                  try{
                    setInProgress(true);
                    setTotalPrice(withAnalysis(await applyVoucher()));
                    setShowDialogMessage('Voucher applied')
                  }
                  finally{
                    setInProgress(false);
                  }
                }}>{renderNextLabel('Apply')}</button>
            </div>

          </div>}
          <div className="bold ft-20 flex "><div className='ml-auto'>{discountApplied}</div></div>
      </section>
      {!affiliatePrePaidToken &&
        <section>
          <div className={'flex-inline accept-condition-check label-box ft-22 mt-20p'} 
            >
            <div className='flex-inline-left'>
              <div className="label">Add a Capacity Snapshot Analysis for <b>£{MENTAL_CAPACITY_ANALYSIS_PRICE}</b> </div>
            </div>
            {analysis 
              ? <Button id="remove-analysis-btn"className='red-button xs' onClick={() => setAnalysis(false)}>Remove</Button>
              : <Button id="add-analysis-btn" className='green-button xs' onClick={() => setAnalysis(true)}>Add</Button>
            }
          </div>
          <div className="text-box ft-18 lower-label">
              Our Capacity Snapshot Analysis provides feedback within 2 working days with a risk rating and suggested next steps.
          </div>
        </section>}

      {/* {analysis &&
        <div className="label-box ft-22">
          <div className="label">Do you want an expert analysis of your Capacity Snapshot?:</div>
          <div className="bold">£{MENTAL_CAPACITY_ANALYSIS_PRICE}</div>
        </div>
      } */}

      {isUserLifetimeSubscriptionActive === false &&
        <section>
          <div className={'flex-inline accept-condition-check ft-22'}>
            <div className='flex-inline-left'>
              <div className="label">Add Lifetime Updates <span className='bold'>free</span> for first year then <span className='bold'> £10 per year</span></div>
              {
                lifeTimeUpdates 
                  ? <Button id="remove-lifetime-updates-btn" className='red-button xs' onClick={() => setLifeTimeUpdates(false)}>Remove</Button>
                  : <Button id="add-lifetime-updates-btn" className='green-button xs' onClick={() => setLifeTimeUpdates(true)}>Add</Button>
              }
            </div>
          </div>
          {/* <LifeTimeUpdate lifeTimeUpdates={lifeTimeUpdates} setLifeTimeUpdates={setLifeTimeUpdates} /> */}
          <div className="text-box ft-18 lower-label">
            Update your Capacity Snapshot whenever you update your will without paying another Account Setup Fee. Access for executors without charge (saving £350).
          </div>
        </section>
      }
      <div className="flex-inline label-box ft-22 ml-24 mt-12" data-testid="amount-due">
        <div className="label bold">Amount due:</div>
        <div style={{width:"54px"}}></div>
        <div className="bold">£{totalPrice}</div>
      </div>

      {lifeTimeUpdates &&
        <div className="flex-inline ft-18 ml-32">
          <div className="label">Subscription year 1:</div>
          <div style={{width:"54px"}}></div>
          <div className="bold">£0</div>
        </div>}
      {lifeTimeUpdates &&
        <div className="flex-inline ft-18 ml-32">
          <div className="label">Subscription year 2 onwards:</div>
        <div style={{width:"54px"}}></div>
          <div className="bold">£10 per year</div>
        </div>}

      <div className={`flex-inline-left ml-24 accept-condition-check ft-22 ${(nextClicked && !accepted) ? "invalid" : ''}`} onClick={() => setAccepted(!accepted)}>
        <Checkbox id='termsAndConditionsCheckbox' checked={accepted} />
        <div className="font-light label">I agree to the <a className="blue" rel="noreferrer" href={LANDING_URL + '/terms-and-conditions'} target="_blank">terms and conditions</a></div>
      </div>

      <Button 
        id='next-btn'
        variant="contained"
        color="primary"
        type="submit"
        className="green-submit-button"
      >
        {renderNextLabel('Confirm')}
      </Button>

      {showDialogMessage && renderDialog( )}
    </form>
    
  )
};

export default YourOrder;
